import { Button, Stack, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { AuthPageLayout } from "@/components/auth";
import SignInPanel from "@/components/auth/SignInPanel";
import { useAuth } from "@/contexts/AuthContext";
import { User } from "@/types/auth";
import { UserRole } from "@/utils/roleManager";

export default function SsoCallback() {
  const { t } = useTranslation();
  const { loginWithSso } = useAuth();
  const navigate = useNavigate();
  const [params] = useState(() => new URLSearchParams(window.location.search));
  const [failed, setFailed] = useState(false);
  const completion = useRef<Promise<User> | null>(null);

  useEffect(() => {
    // Remove the authorization code from browser history immediately.
    window.history.replaceState(window.history.state, "", window.location.pathname);
    let active = true;
    const state = params.get("state");
    const code = params.get("code");
    if (params.has("error") || !state || !code) {
      setFailed(true);
      return;
    }
    // Reuse the same exchange when StrictMode re-runs the effect.
    completion.current ??= loginWithSso(state, code);
    void completion.current
      .then((user) => {
        if (active) {
          void navigate(
            user.role === UserRole.ASSOCIATE ? "/live-view/fitting-rooms" : "/store-performance",
            { replace: true },
          );
        }
      })
      .catch(() => {
        if (active) setFailed(true);
      });
    return () => {
      active = false;
    };
  }, [params, loginWithSso, navigate]);

  return (
    <AuthPageLayout>
      <SignInPanel loading={!failed}>
        <Stack spacing={3} sx={{ py: { xs: 6, sm: 8 }, alignItems: "center", textAlign: "center" }}>
          <Typography role="alert" variant="body2" color="error">
            {t("login.errors.ssoCallbackFailed")}
          </Typography>
          <Button component={Link} to="/login" variant="contained">
            {t("login.actions.backToLogin")}
          </Button>
        </Stack>
      </SignInPanel>
    </AuthPageLayout>
  );
}
